/**
 * Puerto Rico ゲーム終了処理
 * @see docs/specifications/logic/puerto_rico/game_end_override.md
 */

import type { PuertoRicoGameState } from '../../types';
import { PUERTO_RICO_SCORING } from '../../../data/map/puerto_rico/config';

/**
 * ゲーム終了理由
 */
export type GameEndReason = 'VICTORY' | 'DEFEAT_BLACK_CUBES' | 'BANKRUPTCY';

/**
 * スコア内訳
 */
export interface ScoreBreakdown {
  /** ゲーム終了時の収入レベル */
  originalIncomeLevel: number;
  /** 残留黒キューブ数 */
  remainingBlackCubes: number;
  /** 黒キューブペナルティ */
  blackCubePenalty: number;
  /** ペナルティ適用後の収入レベル */
  penalizedIncomeLevel: number;
  /** 収入ポイント（収入レベル × 3） */
  incomePoints: number;
  /** 線路リンクポイント */
  linkPoints: number;
  /** 株式ペナルティ */
  sharePenalty: number;
}

/**
 * ゲーム終了結果
 */
export interface GameEndResult {
  /** ゲームが10ターン完了したか */
  completed: boolean;
  /** 勝利したか */
  victory: boolean;
  /** 終了理由 */
  reason: GameEndReason;
  /** 最終スコア（敗北時はnull） */
  finalScore: number | null;
  /** スコア内訳（勝利時のみ） */
  scoreBreakdown?: ScoreBreakdown;
}

/**
 * マップ上の黒キューブをカウント
 *
 * @param G - ゲーム状態
 * @returns 残留黒キューブ数
 */
export function countBlackCubesOnMap(G: PuertoRicoGameState): number {
  let count = 0;

  for (const hex of G.map) {
    // 町に配置されているキューブをカウント
    if (hex.town && hex.goods) {
      for (const cube of hex.goods) {
        if (cube === 'black') {
          count++;
        }
      }
    }
  }

  return count;
}

/**
 * 袋に残っている黒キューブをカウント
 *
 * @param G - ゲーム状態
 * @returns 袋に残っている黒キューブ数
 */
export function countBlackCubesInBag(G: PuertoRicoGameState): number {
  return G.cubesInBag.filter(cube => cube === 'black').length;
}

/**
 * 総黒キューブ数をカウント（マップ上 + 袋）
 *
 * @param G - ゲーム状態
 * @returns 総黒キューブ数
 */
export function countTotalBlackCubes(G: PuertoRicoGameState): number {
  return countBlackCubesOnMap(G) + countBlackCubesInBag(G);
}

/**
 * 黒キューブペナルティを計算
 *
 * @param remainingBlackCubes - 残留黒キューブ数
 * @returns ペナルティ値（収入レベルから減算する値）
 */
export function calculateBlackCubePenalty(remainingBlackCubes: number): number {
  return remainingBlackCubes * PUERTO_RICO_SCORING.blackCubePenaltyPerCube;
}

/**
 * 最終スコアを計算
 *
 * @param penalizedIncomeLevel - ペナルティ適用後の収入レベル
 * @param completedLinks - 完成リンク数
 * @param sharesIssued - 発行株式数
 * @returns 最終スコア
 */
export function calculateFinalScore(
  penalizedIncomeLevel: number,
  completedLinks: number,
  sharesIssued: number
): number {
  const scoring = PUERTO_RICO_SCORING.victoryScoring;

  const incomePoints = penalizedIncomeLevel * scoring.incomeMultiplier;
  const linkPoints = completedLinks * scoring.linkMultiplier;
  const sharePenalty = sharesIssued * Math.abs(scoring.sharesPenaltyMultiplier);

  return incomePoints + linkPoints - sharePenalty;
}

/**
 * Puerto Rico ゲーム終了処理
 *
 * 10ターン終了時に呼び出され、黒キューブペナルティを適用後、
 * 勝敗判定とスコア計算を行う。
 *
 * @param G - ゲーム状態
 * @returns ゲーム終了結果
 */
export function endGamePuertoRico(G: PuertoRicoGameState): GameEndResult {
  const playerId = '0';
  const player = G.players[playerId];

  // 1. 倒産チェック
  if (player.isBankrupt) {
    G.isGameOver = true;
    G.endReason = 'BANKRUPTCY';
    player.finalScore = null;
    player.isVictorious = false;

    return {
      completed: false,
      victory: false,
      reason: 'BANKRUPTCY',
      finalScore: null
    };
  }

  // 2. 黒キューブカウント（マップ上のみ、袋は含まない）
  const remainingBlackCubes = countBlackCubesOnMap(G);

  // 3. 黒キューブペナルティ適用
  const blackCubePenalty = calculateBlackCubePenalty(remainingBlackCubes);
  const penalizedIncomeLevel = player.incomeLevel - blackCubePenalty;

  // 4. 最終スコア計算（勝敗判定に使用）
  const completedLinkCount = G.completedLinks.length;
  const finalScore = calculateFinalScore(
    penalizedIncomeLevel,
    completedLinkCount,
    player.sharesIssued
  );

  const scoring = PUERTO_RICO_SCORING.victoryScoring;
  const scoreBreakdown: ScoreBreakdown = {
    originalIncomeLevel: player.incomeLevel,
    remainingBlackCubes,
    blackCubePenalty,
    penalizedIncomeLevel,
    incomePoints: penalizedIncomeLevel * scoring.incomeMultiplier,
    linkPoints: completedLinkCount * scoring.linkMultiplier,
    sharePenalty: player.sharesIssued * Math.abs(scoring.sharesPenaltyMultiplier)
  };

  // 5. 敗北判定（最終スコアが0未満の場合）
  if (finalScore < 0) {
    G.isGameOver = true;
    G.endReason = 'DEFEAT_BLACK_CUBES';
    player.finalScore = finalScore;
    player.isVictorious = false;

    return {
      completed: true,
      victory: false,
      reason: 'DEFEAT_BLACK_CUBES',
      finalScore,
      scoreBreakdown
    };
  }

  // 6. 勝利: プレイヤー情報更新
  player.finalScore = finalScore;
  player.isVictorious = true;

  // 7. ゲーム状態更新
  G.isGameOver = true;
  G.endReason = 'VICTORY';
  G.finalScores = { [playerId]: finalScore };

  return {
    completed: true,
    victory: true,
    reason: 'VICTORY',
    finalScore,
    scoreBreakdown
  };
}

/**
 * ゲーム終了条件を判定
 *
 * @param G - ゲーム状態
 * @returns ゲーム終了すべきなら true
 */
export function shouldEndGame(G: PuertoRicoGameState): boolean {
  // 10ターン終了
  if (G.actualTurn > 10) {
    return true;
  }

  // 倒産
  const playerId = '0';
  const player = G.players[playerId];
  if (player.isBankrupt) {
    return true;
  }

  return false;
}
